import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    Money,
    ObjectRef,
    PageHeader,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { ReserveScheduleRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/reserve-schedule';
const PAGE_KEY = 'reserve-schedule';

export const reserveSchedulePrefetch = adminListPrefetch(PAGE_KEY, PATH);

const scheduleFilters = filterDefs(PATH, {
    state: 'State',
    merchant_id: 'Merchant',
    range: 'Release date',
});

const columns: ListColumnDef<ReserveScheduleRow>[] = [
    {
        key: 'charge_id',
        aliases: ['charge'],
        label: 'Charge',
        boardCard: true,
        locked: 'start',
        csv: (r) => r.charge_id,
        cell: (r) => (
            <Link
                to={`/money/charges/${r.charge_id}`}
                className="font-mono text-xs hover:underline"
            >
                {r.charge_id}
            </Link>
        ),
        noTruncate: true,
    },
    {
        key: 'merchant_id',
        aliases: ['merchant'],
        label: 'Merchant',
        csv: (r) => r.merchant_id,
        cell: (r) => <ObjectRef id={r.merchant_id} />,
    },
    {
        key: 'reserve_cents',
        aliases: ['reserve'],
        label: 'Reserve',
        sortKey: 'reserve_cents',
        boardCard: true,
        csv: (r) => r.reserve_cents,
        cell: (r) => <Money cents={r.reserve_cents} currency="USD" />,
        noTruncate: true,
    },
    {
        key: 'release_at',
        aliases: ['release'],
        label: 'Releases',
        kind: 'datetime',
        sortKey: 'release_at',
        csv: (r) => r.release_at,
        cell: (r) => <DateTime value={r.release_at} empty="—" />,
        noTruncate: true,
    },
    {
        key: 'state',
        label: 'State',
        csv: (r) => r.state,
        cell: (r) => (
            <StatusBadge
                status={r.state}
                domain={ADMIN_STATUSES.reserveState}
            />
        ),
    },
    {
        key: 'released_at',
        aliases: ['released'],
        label: 'Released',
        kind: 'datetime',
        sortKey: 'released_at',
        csv: (r) => r.released_at ?? '',
        cell: (r) =>
            r.released_at ? <DateTime value={r.released_at} empty="—" /> : '—',
        noTruncate: true,
    },
];

/**
 * BM-62 2.3: every reserve a sale held, with its release date. The daily `pricing:reserve-release` run releases what
 * is due; a refund draws its share first.
 */
export function ReserveSchedulePage() {
    const { state, query } = useAdminList<ReserveScheduleRow>(PAGE_KEY, PATH, {
        columns,
        filters: scheduleFilters,
    });
    return (
        <div>
            <PageHeader
                title="Reserve schedule"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'What each sale held in reserve and when it is released back to the merchant balance. Releases run daily.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="reserves"
                state={state}
                query={query}
                rowKey={(r) => r.charge_id}
                controls={{
                    state: { domain: ADMIN_STATUSES.reserveState },
                }}
            />
        </div>
    );
}
